/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Send, Search, Loader, Sparkles, ChevronRight, AlertCircle,
  RefreshCw, ExternalLink, Brain, X, Bot, Mic
} from 'lucide-react'
import posthog from 'posthog-js'
import adminServices from '../../../services/adminServices'

// ─── GUARDRAILS ────────────────────────────────────────────────────────────────

const GUARDRAIL_PATTERNS = [
  { type: 'self_harm', severity: 'critical',
    re: /\b(kill\s*(my)?self|suicide|suicidal|want\s*to\s*die|end\s*my\s*life|self[\s-]?harm|cut\s*myself|hurt\s*myself|overdose|hang\s*myself|slit\s*my|take\s*my\s*(own\s*)?life|no\s*reason\s*to\s*live|don'?t\s*want\s*to\s*(be\s*here|exist|live)|nothing\s*to\s*live\s*for|i\s*want\s*to\s*die)\b/i },
  { type: 'harm_to_others', severity: 'critical',
    re: /\bhow\s*(can|do|to|could|would|should)\s*(i|one|you|someone|a\s*person)?\s*(kill|murder|hurt|harm|attack|stab|shoot|poison|strangle|beat\s*up|assault|torture|rape|abuse)\s*(some\s*one|someone|a\s*person|people|him|her|them|my)?\b/i },
  { type: 'harm_to_others', severity: 'critical',
    re: /\b(how\s*to\s*)?(kill|murder|assassinate|eliminate|get\s*rid\s*of)\s*(some\s*one|someone|a\s*person|people|him|her|them)\b/i },
  { type: 'harm_to_others', severity: 'critical',
    re: /\b(want\s*to|going\s*to|plan\s*to|thinking\s*(of|about))\s*(kill|hurt|harm|attack|murder|stab|shoot)\s*(some\s*one|someone|a\s*person|people|him|her|them|my)\b/i },
  { type: 'harm_to_others', severity: 'critical',
    re: /\b(ways?\s*to\s*)(kill|murder|hurt|harm|poison|attack)\b/i },
  { type: 'harm_to_others', severity: 'critical',
    re: /\bi\s*(want|need|have)\s*to\s*(kill|murder|hurt|harm|attack|stab|shoot)\s*(some\s*one|someone|a\s*person|people|him|her|them)\b/i },
  { type: 'violence', severity: 'high',
    re: /\b(how\s*to\s*)?(make|build|create|get)\s*(a\s*)?(bomb|bom\b|weapon|explosive|poison|gun|knife\s*to\s*hurt)/i },
  { type: 'crisis', severity: 'critical',
    re: /\b(i\s*can'?t\s*(go\s*on|take\s*it|do\s*this\s*anymore)|feeling\s*hopeless|i\s*(feel|am)\s*(so\s*)?(worthless|hopeless|broken|empty|numb)|mental\s*breakdown|i\s*give\s*up\s*on\s*life)\b/i },
  { type: 'abuse', severity: 'high',
    re: /\b(being\s*abused|someone\s*(is\s*)?(hurting|hitting|beating|abusing)\s*me|domestic\s*violence|sexual\s*abuse|molest(ed)?|assault(ed)?|rape[d]?)\b/i },
  { type: 'guideline_breach', severity: 'low',
    re: /\b(ignore\s*(previous|all|your)\s*(instructions?|rules?|guidelines?|prompt|system)|pretend\s*you\s*(are|have\s*no)|you\s*are\s*now\s*a|jailbreak|bypass\s*(filter|restriction|safety)|act\s*as\s*(if\s*you\s*(have\s*no|are\s*not)|a\s*different)|forget\s*(your\s*)?(rules?|guidelines?|instructions?))\b/i },
]

const CRISIS_MSG = `I'm not able to help with that. If you or someone you know is in danger, please contact emergency services immediately (112 or 911). Is there something about the course I can help you with?`
const HARM_MSG = `I can't help with that request. If someone is in immediate danger, please call emergency services (112 or 911) right away. Is there something about the course I can help you with?`
const SCOPE_MSG = `I can only answer questions related to this course content. Please ask something covered in the course.`

function frontendGuardrail(text) {
  if (!text || !text.trim()) return { flagged: false }
  const normalised = text.replace(/[^\w\s']/g, ' ').replace(/\s+/g, ' ').trim()
  for (const rule of GUARDRAIL_PATTERNS) {
    if (rule.re.test(normalised)) {
      const safeResponse = rule.type === 'guideline_breach' ? SCOPE_MSG
        : (rule.type === 'harm_to_others' || rule.type === 'violence') ? HARM_MSG
        : CRISIS_MSG
      return { flagged: true, type: rule.type, severity: rule.severity, safeResponse }
    }
  }
  return { flagged: false }
}

// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function useDraggablePosition(storageKey) {
  const getInitial = () => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) return JSON.parse(saved)
    } catch (_) {}
    return { x: window.innerWidth - 80, y: window.innerHeight - 100 }
  }
  const [pos, setPos] = useState(getInitial)
  const dragging = useRef(false)
  const offset = useRef({ x: 0, y: 0 })
  const btnRef = useRef(null)

  const onMouseDown = useCallback(e => {
    dragging.current = true
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
    e.preventDefault()
  }, [pos])

  const onTouchStart = useCallback(e => {
    dragging.current = true
    const t = e.touches[0]
    offset.current = { x: t.clientX - pos.x, y: t.clientY - pos.y }
  }, [pos])

  useEffect(() => {
    const onMove = e => {
      if (!dragging.current) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      const newX = Math.max(0, Math.min(window.innerWidth - 56, clientX - offset.current.x))
      const newY = Math.max(0, Math.min(window.innerHeight - 56, clientY - offset.current.y))
      setPos({ x: newX, y: newY })
    }
    const onEnd = () => {
      if (dragging.current) {
        dragging.current = false
        setPos(p => {
          try { localStorage.setItem(storageKey, JSON.stringify(p)) } catch (_) {}
          return p
        })
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
  }, [storageKey])

  return { pos, btnRef, onMouseDown, onTouchStart }
}

// ─── SPEECH RECOGNITION HOOK (FIXED) ─────────────────────────────────────────

function useSpeechRecognition(onFinal, onInterim) {
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef(null)
  // Guard: ensures we only fire the send trigger once per recording session,
  // regardless of how many times onend fires or whether stopAndSend races with it.
  const sentRef = useRef(false)
  const supported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

  const startListening = useCallback(() => {
    if (!supported) {
      alert('Speech recognition is not supported in this browser. Try Chrome or Edge.')
      return
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    const rec = new SR()
    rec.lang = 'en-IN'
    rec.interimResults = true
    rec.maxAlternatives = 1
    rec.continuous = false

    // Reset the guard for this new session
    sentRef.current = false

    rec.onresult = e => {
      let interimText = ''
      let finalText = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript
        if (e.results[i].isFinal) finalText += t
        else interimText += t
      }
      // Show live interim text in the input box
      if (interimText) onInterim(interimText)
      // Commit final text to input — never trigger send here,
      // onend is the single canonical place where sending happens.
      if (finalText) onFinal(finalText, false)
    }

    rec.onerror = () => {
      setListening(false)
    }

    rec.onend = () => {
      setListening(false)
      // Only fire the send trigger once — guard prevents double-fire
      // when stopAndSend() and onend race each other.
      if (!sentRef.current) {
        sentRef.current = true
        // Small delay to let React flush the searchQuery state update
        setTimeout(() => onFinal(null, true), 80)
      }
    }

    recognitionRef.current = rec
    rec.start()
    setListening(true)
  }, [supported, onFinal, onInterim])

  const stopAndSend = useCallback(() => {
    // Mark as sent BEFORE calling .stop() so the onend handler that fires
    // immediately after .stop() sees the flag and does nothing.
    sentRef.current = true
    if (recognitionRef.current) recognitionRef.current.stop()
    setListening(false)
    // Manually trigger the send since onend will be suppressed
    setTimeout(() => onFinal(null, true), 80)
  }, [onFinal])

  const toggle = useCallback(() => {
    if (listening) {
      stopAndSend()
    } else {
      startListening()
    }
  }, [listening, startListening, stopAndSend])

  return { listening, toggle, supported }
}

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────

const statusClassMap = {
  idle: 'bg-gray-300',
  loading: 'bg-amber-400 animate-pulse',
  ready: 'bg-emerald-400',
  error: 'bg-red-400',
}
const StatusDot = ({ status }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${statusClassMap[status] || statusClassMap.idle}`} />
)

const SkeletonBlock = ({ className = '' }) => (
  <div className={`bg-gray-100 rounded-xl animate-pulse ${className}`} />
)

const KBNotReadyBanner = () => (
  <div className='mx-0 mb-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5'>
    <AlertCircle className='w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5' />
    <div>
      <p className='text-xs font-semibold text-amber-800 mb-0.5'>AI not set up yet</p>
      <p className='text-xs text-amber-700 leading-relaxed'>
        The knowledge base for this course hasn't been generated yet. Ask your admin to click{' '}
        <strong>Generate KB</strong> on the course management page.
      </p>
    </div>
  </div>
)

const ThinkingBubble = () => (
  <div className='flex items-end gap-2 px-4'>
    <div className='w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0'>
      <Bot className='w-3.5 h-3.5 text-white' />
    </div>
    <div className='bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 flex items-center gap-2 shadow-sm'>
      <span className='flex gap-1'>
        <span className='w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce' style={{ animationDelay: '0ms' }} />
        <span className='w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce' style={{ animationDelay: '150ms' }} />
        <span className='w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce' style={{ animationDelay: '300ms' }} />
      </span>
    </div>
  </div>
)

const ReferencePills = ({ urls }) => {
  if (!urls || urls.length === 0) return null
  return (
    <div className='mt-2.5 pt-2 border-t border-gray-100'>
      <p className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>References</p>
      <div className='flex flex-wrap gap-1.5'>
        {urls.map((url, i) => {
          let label = url
          try { label = new URL(url).hostname.replace('www.', '') } catch (_) {}
          return (
            <a key={i} href={url} target='_blank' rel='noreferrer'
              className='inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full text-xs text-blue-700 transition-colors'>
              <ExternalLink className='w-3 h-3 flex-shrink-0' />
              <span className='max-w-[120px] truncate'>{label}</span>
            </a>
          )
        })}
      </div>
    </div>
  )
}

const ModuleContextChips = ({ moduleContext, onNavigate }) => {
  if (!moduleContext || moduleContext.length === 0) return null
  return (
    <div className='mt-2.5 pt-2 border-t border-gray-100'>
      <p className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Found in</p>
      <div className='flex flex-wrap gap-1.5'>
        {moduleContext.map((ctx, i) => (
          <button
            key={i}
            onClick={() => onNavigate && onNavigate(ctx)}
            className='inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 hover:border-indigo-400 rounded-lg text-xs text-indigo-700 transition-all group'
          >
            <svg className='w-3 h-3 flex-shrink-0 text-indigo-400 group-hover:text-indigo-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
            </svg>
            <span className='font-medium truncate max-w-[140px]'>{ctx.module_name}</span>
            {ctx.sub_module_name && (
              <>
                <ChevronRight className='w-3 h-3 flex-shrink-0 text-indigo-300' />
                <span className='truncate max-w-[120px] text-indigo-500'>{ctx.sub_module_name}</span>
              </>
            )}
            <svg className='w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 text-indigo-500 transition-opacity ml-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── CHAT PANEL ───────────────────────────────────────────────────────────────

const ChatPanel = ({
  messages, answerLoading, initialQuestions, kbStatus, loadingInitial,
  searchQuery, searchResults, isSearching, dropdownAbove, courseName,
  onQuestionClick, onSend, onSearchInput, onKeyDown, onClearChat, onClose,
  showClose, messagesContainerRef, messagesEndRef, inputRef, inputWrapperRef,
  onNavigateToModule,
  micListening,
  onMicToggle,
}) => {
  const dotStatus = answerLoading ? 'loading' : kbStatus === 'done' ? 'ready' : 'idle'
  const statusLabel = answerLoading ? 'Thinking…' : kbStatus === 'done' ? 'Ready' : 'Setting up…'

  const renderEmptyState = () => (
    <div className='flex flex-col h-full px-4 py-5 overflow-y-auto' style={{ overscrollBehavior: 'contain' }}>
      <div className='flex items-start gap-3 mb-5'>
        <div className='w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md'>
          <Sparkles className='w-5 h-5 text-white' />
        </div>
        <div>
          <p className='text-sm font-bold text-gray-900'>AI Course Assistant</p>
          <p className='text-xs text-gray-500 mt-0.5'>
            Ask anything about <span className='font-semibold text-blue-600'>{courseName}</span>
          </p>
        </div>
      </div>

      {kbStatus === 'not_started' && <KBNotReadyBanner />}

      {loadingInitial ? (
        <div className='space-y-2.5'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>Suggested Questions</p>
          {[1, 2, 3, 4].map(i => <SkeletonBlock key={i} className='h-12 w-full' />)}
        </div>
      ) : initialQuestions.length > 0 ? (
        <div className='space-y-2'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>Suggested Questions</p>
          {initialQuestions.map((q, i) => (
            <button key={q.id || i} onClick={() => onQuestionClick(q)}
              className='w-full text-left flex items-start gap-2.5 px-3.5 py-3 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 transition-all duration-150 group shadow-sm'>
              <ChevronRight className='w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-300 group-hover:text-blue-500 transition-colors' />
              <span className='text-sm text-gray-700 group-hover:text-gray-900 leading-snug'>{q.question_text}</span>
            </button>
          ))}
        </div>
      ) : kbStatus !== 'not_started' ? (
        <p className='text-sm text-gray-500 text-center py-8'>Type a question below to get started.</p>
      ) : null}
    </div>
  )

  const renderBubble = msg => {
    if (msg.type === 'user') {
      return (
        <div key={msg.id} className='flex justify-end px-4'>
          <div className='max-w-[80%] bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl rounded-br-none px-4 py-2.5 text-sm shadow-sm leading-relaxed'>
            {msg.text}
          </div>
        </div>
      )
    }

    const bubbleCls = msg.isError
      ? 'bg-red-50 border border-red-200 text-red-700'
      : msg.isOutOfScope
        ? 'bg-amber-50 border border-amber-200 text-amber-800'
        : 'bg-white border border-gray-200 text-gray-800'

    return (
      <div key={msg.id} className='flex flex-col gap-2 px-4'>
        <div className='flex items-end gap-2'>
          <div className='w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mb-0.5'>
            <Bot className='w-3.5 h-3.5 text-white' />
          </div>
          <div className={`max-w-[88%] rounded-2xl rounded-bl-none px-4 py-3 text-sm shadow-sm ${bubbleCls}`}>
            {msg.isOutOfScope && (
              <p className='text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-1'>Out of scope</p>
            )}
            <p className='leading-relaxed'>{msg.answer}</p>
            <ModuleContextChips moduleContext={msg.moduleContext} onNavigate={onNavigateToModule} />
            <ReferencePills urls={msg.references} />
            {msg.iqQuestions && msg.iqQuestions.length > 0 && (
              <div className='mt-2.5 pt-2 border-t border-gray-100 flex items-center gap-1.5'>
                <Brain className='w-3.5 h-3.5 text-purple-500' />
                <p className='text-xs text-purple-700 font-medium'>
                  {msg.iqQuestions.length} IQ practice question{msg.iqQuestions.length > 1 ? 's' : ''} available
                </p>
              </div>
            )}
          </div>
        </div>

        {msg.relatedQuestions && msg.relatedQuestions.length > 0 && (
          <div className='ml-9 flex gap-2 overflow-x-auto pb-1 scrollbar-hide'>
            {msg.relatedQuestions.slice(0, 5).map((rq, i) => {
              const text = rq.question_text || rq.text || rq
              return (
                <button key={rq.id || i} onClick={() => onQuestionClick(rq)}
                  className='flex-shrink-0 text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-600 hover:text-blue-700 border border-gray-200 hover:border-blue-300 transition-all whitespace-nowrap max-w-[200px] truncate'>
                  {text}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='flex flex-col bg-white overflow-hidden w-full h-full' style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className='flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 flex-shrink-0'>
        <div className='flex items-center gap-2.5'>
          <div className='w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center'>
            <Sparkles className='w-4 h-4 text-white' />
          </div>
          <div>
            <p className='text-sm font-bold text-white leading-none'>Course AI</p>
            <div className='flex items-center gap-1.5 mt-0.5'>
              <StatusDot status={dotStatus} />
              <p className='text-[11px] text-blue-100'>{statusLabel}</p>
            </div>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          {messages.length > 0 && (
            <button onClick={onClearChat} title='Clear chat'
              className='p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white'>
              <RefreshCw className='w-3.5 h-3.5' />
            </button>
          )}
          {showClose && (
            <button onClick={onClose} className='p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white'>
              <X className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>

      {/* ── Messages ───────────────────────────────────────────── */}
      <div
        ref={messagesContainerRef}
        className='flex-1 bg-gray-50'
        style={{
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          isolation: 'isolate',
        }}
        onWheel={e => e.stopPropagation()}
      >
        {messages.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className='py-4 space-y-4'>
            {messages.map(msg => renderBubble(msg))}
            {answerLoading && <ThinkingBubble />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* ── Input ──────────────────────────────────────────────── */}
      <div className='flex-shrink-0 px-3 py-3 bg-white border-t border-gray-100'>
        <div ref={inputWrapperRef} className='relative'>

          {/* Search dropdown */}
          {searchResults.length > 0 && (
            <div className={`absolute left-0 right-0 z-50 ${dropdownAbove ? 'bottom-full mb-2' : 'top-full mt-2'}
              bg-white border border-gray-200 rounded-xl shadow-xl max-h-44 overflow-y-auto`}
              style={{ overscrollBehavior: 'contain' }}>
              {searchResults.map((q, i) => (
                <button key={q.id || i} onClick={() => onQuestionClick(q)}
                  className='w-full text-left text-xs px-3.5 py-3 hover:bg-blue-50 text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors flex items-start gap-2'>
                  <Search className='w-3 h-3 flex-shrink-0 mt-0.5 text-gray-400' />
                  {q.question_text}
                </button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className={`flex items-center gap-2 rounded-2xl px-3.5 py-2.5 transition-all duration-150 border
            ${micListening
              ? 'bg-red-50 border-red-300 ring-2 ring-red-300'
              : 'bg-gray-100 border-transparent focus-within:ring-2 focus-within:ring-blue-500 focus-within:bg-white focus-within:border-blue-200'
            }`}>

            {/* Left icon: search / spinner / mic-wave */}
            {micListening ? (
              <span className='flex gap-[3px] items-end h-4 flex-shrink-0'>
                {[0, 1, 2, 3].map(i => (
                  <span
                    key={i}
                    className='w-[3px] rounded-full bg-red-500'
                    style={{
                      height: '100%',
                      animation: `micWave 0.8s ease-in-out infinite`,
                      animationDelay: `${i * 0.12}s`,
                    }}
                  />
                ))}
              </span>
            ) : isSearching ? (
              <Loader className='w-4 h-4 text-gray-400 animate-spin flex-shrink-0' />
            ) : (
              <Search className='w-4 h-4 text-gray-400 flex-shrink-0' />
            )}

            <input
              ref={inputRef}
              type='text'
              placeholder={micListening ? 'Listening… tap mic to send' : 'Ask a question…'}
              value={searchQuery}
              onChange={onSearchInput}
              onKeyDown={onKeyDown}
              readOnly={micListening}
              className={`flex-1 bg-transparent text-sm outline-none min-w-0
                ${micListening ? 'text-red-700 placeholder-red-400' : 'text-gray-800 placeholder-gray-400'}`}
            />

            {/* Mic button — always visible */}
            <button
              onClick={onMicToggle}
              title={micListening ? 'Tap to send' : 'Speak your question'}
              className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200
                ${micListening
                  ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg scale-110'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                }`}
            >
              <Mic className='w-3.5 h-3.5' />
            </button>

            {/* Send button — hidden while mic is active */}
            {!micListening && (
              <button
                onClick={onSend}
                disabled={!searchQuery.trim() || answerLoading}
                className='flex-shrink-0 w-8 h-8 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all duration-150 shadow-sm'>
                {answerLoading
                  ? <Loader className='w-3.5 h-3.5 animate-spin' />
                  : <Send className='w-3.5 h-3.5' />}
              </button>
            )}
          </div>
        </div>

        {/* Hint text */}
        <p className={`text-[10px] text-center mt-1.5 transition-colors duration-200 ${micListening ? 'text-red-400' : 'text-gray-400'}`}>
          {micListening
            ? 'Speaking… tap mic button to send'
            : 'AI may make mistakes. Verify important information.'}
        </p>
      </div>

      {/* Keyframe for mic wave animation */}
      <style>{`
        @keyframes micWave {
          0%, 100% { transform: scaleY(0.3); }
          50%       { transform: scaleY(1); }
        }
      `}</style>
    </div>
  )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const CourseAIAssistant = ({
  courseId,
  courseName,
  inline = false,
  onKbStatus,
  initialQuestionsFromParent = null,
  kbStatusFromParent = null,
  onNavigateToModule,
}) => {
  const isMobile = useIsMobile()
  const { pos, onMouseDown, onTouchStart } = useDraggablePosition('ai_fab_pos')

  const [sheetOpen, setSheetOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [initialQuestions, setInitialQuestions] = useState([])
  const [loadingInitial, setLoadingInitial] = useState(false)
  const [answerLoading, setAnswerLoading] = useState(false)
  const [kbStatus, setKbStatus] = useState('idle')
  const [dropdownAbove, setDropdownAbove] = useState(false)
  const [sessionId, setSessionId] = useState(null)

  const messagesContainerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const searchTimeout = useRef(null)
  const inputRef = useRef(null)
  const inputWrapperRef = useRef(null)
  const isDragged = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const loadedForCourseId = useRef(null)

  // Tracks the interim (in-progress) text so we can replace it with the final
  const interimRef = useRef('')

  // A ref to handleSend so speech callbacks don't get stale closures
  const handleSendRef = useRef(null)

  // ── Speech recognition callbacks ───────────────────────────────────────────
  const onFinal = useCallback((transcript, shouldSend) => {
    if (transcript) {
      setSearchQuery(prev => {
        // Replace trailing interim text with the confirmed final text
        const base = interimRef.current
          ? prev.slice(0, prev.lastIndexOf(interimRef.current)).trimEnd()
          : prev.trimEnd()
        interimRef.current = ''
        return base ? base + ' ' + transcript : transcript
      })
    }
    if (shouldSend) {
      // Small delay to let React flush the state update before sending
      setTimeout(() => {
        if (handleSendRef.current) handleSendRef.current()
      }, 80)
    }
  }, [])

  const onInterim = useCallback(interim => {
    setSearchQuery(prev => {
      // Replace the previous interim chunk with the new one
      const base = interimRef.current
        ? prev.slice(0, prev.lastIndexOf(interimRef.current)).trimEnd()
        : prev.trimEnd()
      interimRef.current = interim
      return base ? base + ' ' + interim : interim
    })
  }, [])

  const { listening: micListening, toggle: toggleMic } = useSpeechRecognition(onFinal, onInterim)

  // Lock body scroll on mobile sheet
  useEffect(() => {
    if (isMobile && sheetOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isMobile, sheetOpen])

  // Auto-scroll messages container
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }, [messages, answerLoading])

  // Dropdown direction
  useEffect(() => {
    if (searchResults.length === 0 || !inputWrapperRef.current) return
    const rect = inputWrapperRef.current.getBoundingClientRect()
    setDropdownAbove(window.innerHeight - rect.bottom < 180)
  }, [searchResults])

  useEffect(() => {
    if (initialQuestionsFromParent && initialQuestionsFromParent.length > 0) {
      setInitialQuestions(initialQuestionsFromParent)
      setKbStatus(kbStatusFromParent || 'done')
      setLoadingInitial(false)
      return
    }
    if (kbStatusFromParent && kbStatusFromParent !== 'idle') {
      setKbStatus(kbStatusFromParent)
      setLoadingInitial(false)
      return
    }
    if (!courseId || loadedForCourseId.current === courseId) return
    loadedForCourseId.current = courseId
    // loadInitialQuestions()
  }, [courseId, initialQuestionsFromParent, kbStatusFromParent])

  const loadInitialQuestions = async () => {
    setLoadingInitial(true)
    try {
      const res = await adminServices.getInitialQuestions(courseId)
      if (res.ok && res.data?.questions?.length > 0) {
        setInitialQuestions(res.data.questions)
        setKbStatus('done')
        onKbStatus && onKbStatus(true)
      } else {
        setInitialQuestions([])
        setKbStatus('not_started')
        onKbStatus && onKbStatus(false)
      }
    } catch {
      setKbStatus('error')
      onKbStatus && onKbStatus(false)
    } finally {
      setLoadingInitial(false)
    }
  }

  const handleSearchInput = e => {
    const val = e.target.value
    setSearchQuery(val)
    clearTimeout(searchTimeout.current)
    if (!val.trim()) {
      setSearchResults([])
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await adminServices.searchQuestions(courseId, val.trim())
        if (!answerLoading) {
          setSearchResults(res.ok && res.data?.questions?.length > 0 ? res.data.questions : [])
        }
      } catch {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 400)
  }

  const clearSearchSuggestions = () => {
    setSearchResults([])
    clearTimeout(searchTimeout.current)
    setIsSearching(false)
  }

  const appendUserMessage = text =>
    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text }])

  const appendAIMessage = payload =>
    setMessages(prev => [...prev, { id: Date.now() + 1, type: 'ai', ...payload }])

  const appendErrorMessage = () =>
    setMessages(prev => [...prev, {
      id: Date.now() + 1, type: 'ai',
      answer: 'Sorry, something went wrong. Please try again.',
      isError: true, relatedQuestions: [], iqQuestions: [], references: [],
    }])

  const ensureSession = async () => {
    if (sessionId) return sessionId
    try {
      const res = await adminServices.createAiSession(courseId)
      if (res.ok && res.data?.sessionId) {
        setSessionId(res.data.sessionId)
        return res.data.sessionId
      }
    } catch (_) {}
    return null
  }

  const handleQuestionClick = async question => {
    const questionText = question.question_text || question.text || question
    const questionId = question.id || null

    const guard = frontendGuardrail(questionText)
    clearSearchSuggestions()
    setSearchQuery('')
    appendUserMessage(questionText)

    if (guard.flagged) {
      appendAIMessage({ answer: guard.safeResponse, isOutOfScope: false, relatedQuestions: [], iqQuestions: [], references: [] })
      const sid = await ensureSession()
      adminServices.askQuestion(courseId, null, questionText, false, sid).catch(() => {})
      return
    }

    setAnswerLoading(true)
    const sid = await ensureSession()
    try {
      const res = await adminServices.askQuestion(courseId, questionId, questionId ? null : questionText, false, sid)
      if (res.ok && res.data) {
        appendAIMessage({
          answer: res.data.answer,
          relatedQuestions: res.data.related_questions || [],
          iqQuestions: res.data.tagged_iq_questions || [],
          references: res.data.tagged_references || [],
          isOutOfScope: res.data.is_out_of_scope || false,
          moduleContext: res.data.module_context || [],
        })
      } else appendErrorMessage()
    } catch {
      appendErrorMessage()
    } finally {
      setAnswerLoading(false)
    }
  }

  const handleSend = async () => {
    // Clear any leftover interim ref
    interimRef.current = ''

    const text = searchQuery.trim()
    // answerLoading check acts as a last-resort guard against stale speech callbacks
    if (!text || answerLoading) return

    const guard = frontendGuardrail(text)
    clearSearchSuggestions()
    setSearchQuery('')
    appendUserMessage(text)

    if (guard.flagged) {
      appendAIMessage({ answer: guard.safeResponse, isOutOfScope: false, relatedQuestions: [], iqQuestions: [], references: [] })
      const sid = await ensureSession()
      adminServices.askQuestion(courseId, null, text, false, sid).catch(() => {})
      return
    }

    setAnswerLoading(true)
    const sid = await ensureSession()
    try {
      posthog.capture('ai_assistant_message_sent', {
        course_id: courseId,
        message_length: text.length,
      });
      const res = await adminServices.askQuestion(courseId, null, text, true, sid)
      if (res.ok && res.data) {
        appendAIMessage({
          answer: res.data.answer,
          relatedQuestions: res.data.related_questions || [],
          iqQuestions: res.data.tagged_iq_questions || [],
          references: res.data.tagged_references || [],
          isOutOfScope: res.data.is_out_of_scope || false,
          moduleContext: res.data.module_context || [],
        })
      } else appendErrorMessage()
    } catch {
      appendErrorMessage()
    } finally {
      setAnswerLoading(false)
    }
  }

  // Keep the ref in sync so the speech callback always calls the latest handleSend
  handleSendRef.current = handleSend

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      clearSearchSuggestions()
      handleSend()
    }
    if (e.key === 'Escape') {
      clearSearchSuggestions()
    }
  }

  const sharedProps = {
    messages, answerLoading, initialQuestions, kbStatus, loadingInitial,
    searchQuery, searchResults, isSearching, dropdownAbove, courseName,
    onQuestionClick: handleQuestionClick,
    onSend: handleSend,
    onSearchInput: handleSearchInput,
    onKeyDown: handleKeyDown,
    onClearChat: () => { setMessages([]); setSessionId(null) },
    messagesContainerRef, messagesEndRef, inputRef, inputWrapperRef,
    onNavigateToModule,
    micListening,
    onMicToggle: toggleMic,
  }

  // ── Mobile: FAB + bottom sheet ─────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        {!sheetOpen && (
          <div
            style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 50, touchAction: 'none', userSelect: 'none' }}
            onMouseDown={e => {
              dragStart.current = { x: e.clientX, y: e.clientY }
              isDragged.current = false
              onMouseDown(e)
            }}
            onMouseMove={e => {
              if (Math.abs(e.clientX - dragStart.current.x) > 4 || Math.abs(e.clientY - dragStart.current.y) > 4) {
                isDragged.current = true
              }
            }}
            onMouseUp={() => { if (!isDragged.current) setSheetOpen(true) }}
            onTouchStart={e => {
              dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
              isDragged.current = false
              onTouchStart(e)
            }}
            onTouchEnd={e => {
              e.preventDefault()
              const t = e.changedTouches[0]
              if (Math.abs(t.clientX - dragStart.current.x) < 20 && Math.abs(t.clientY - dragStart.current.y) < 20) {
                isDragged.current = false
                setSheetOpen(true)
              }
            }}
            onClick={() => setSheetOpen(true)}
          >
            <div className='relative cursor-pointer'>
              <div className='absolute inset-0 bg-blue-500 rounded-full blur-md opacity-60' />
              <div className='relative bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full w-14 h-14 shadow-2xl flex items-center justify-center'>
                <Sparkles className='w-6 h-6' />
              </div>
            </div>
          </div>
        )}

        {sheetOpen && (
          <div className='fixed inset-0 z-40 bg-black/50 backdrop-blur-sm'
            onClick={() => setSheetOpen(false)}
            style={{ transition: 'opacity 0.25s ease' }} />
        )}

        <div className='fixed left-0 right-0 bottom-0 z-50 rounded-t-3xl overflow-hidden shadow-2xl'
          style={{
            height: '92dvh',
            transform: sheetOpen ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
          }}>
          <div className='bg-gradient-to-r from-blue-700 to-indigo-600 pt-3 pb-0 flex justify-center cursor-pointer'
            onClick={() => setSheetOpen(false)}>
            <div className='w-10 h-1 bg-white/40 rounded-full mb-0' />
          </div>
          <ChatPanel {...sharedProps} showClose={true} onClose={() => setSheetOpen(false)} />
        </div>

        <style>{`
          .scrollbar-hide::-webkit-scrollbar{display:none}
          .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
        `}</style>
      </>
    )
  }

  // ── Desktop: inline panel ──────────────────────────────────────────────────
  return (
    <>
      <div className='rounded-2xl border border-gray-200 overflow-hidden shadow-sm'
        style={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <ChatPanel {...sharedProps} showClose={false} onClose={null} />
      </div>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar{display:none}
        .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
      `}</style>
    </>
  )
}

CourseAIAssistant.defaultProps = { courseName: 'this course' }
export default CourseAIAssistant